import { useSelector } from 'react-redux';

import { intakeFormUpdateFieldValue } from '~/actions/intakeForm';
import { parseDate } from '~/eds/src/utils/parseDate';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import * as selectors from '~/redux/selectors';
import { QuestionFieldState } from '~/redux/selectors';
import { useAppDispatch } from '~/rootState';
import { Nullable } from '~/types/Nullable';

import { DateWorkflowQuestion } from './DateWorkflowQuestion';
import { WorkflowQuestionFormField } from './types';

const findDateFieldByWellKnownKey = (
  fields: QuestionFieldState[] | Record<string, QuestionFieldState> | undefined,
  wellKnownKey: string,
): QuestionFieldState | undefined => {
  if (!fields) {
    return undefined;
  }

  const fieldsArray = Array.isArray(fields) ? fields : Object.values(fields);

  return fieldsArray.find((field: QuestionFieldState) => {
    return (
      field.type === WorkflowFieldType.Date &&
      field.wellKnownKey === wellKnownKey
    );
  });
};

export const WellKnownDateWorkflowQuestion = ({
  question,
  onChange,
  ...rest
}: WorkflowQuestionFormField<Nullable<string>>) => {
  const dispatch = useAppDispatch();
  const intakeForm = useSelector(selectors.selectIntakeForm);
  const {
    field: { wellKnownKey },
  } = question;

  const isEndDate = wellKnownKey === 'end_date';
  const isStartDate = wellKnownKey === 'start_date';

  const handleDateChange = (val: Nullable<string>, key?: string) => {
    onChange(val, key || 'value');

    // If this is a start_date field, check if we need to update the end_date
    if (isStartDate && val) {
      const endDateField = findDateFieldByWellKnownKey(
        intakeForm.fields,
        'end_date',
      );

      if (endDateField && endDateField.value?.value) {
        const currentEndDate = parseDate(endDateField.value.value);
        const newStartDate = parseDate(val);
        // If the new start date is after the current end date, update end date to match start date
        if (newStartDate > currentEndDate) {
          dispatch(
            intakeFormUpdateFieldValue({
              id: endDateField.id,
              value: {
                ...endDateField.value,
                value: val,
              },
            }),
          );
        }
      }
    }
  };

  // For end_date, find the corresponding start_date to set as minDate
  let minDate: Date | undefined = undefined;
  if (isEndDate) {
    const startDateField = findDateFieldByWellKnownKey(
      intakeForm.fields,
      'start_date',
    );

    if (startDateField && startDateField.value?.value) {
      minDate = parseDate(startDateField.value.value);
    }
  }

  return (
    <DateWorkflowQuestion
      {...rest}
      question={question}
      onChange={handleDateChange}
      minDate={minDate}
    />
  );
};
